/**
 * Putting a survey back the way it was created, and the one line that separates it from deleting.
 *
 * **The damage is a superset of the purge's**, so the guards are the purge's — deliberately the
 * same design as {@link SurveyPurgeDialog}, not a second one invented next to it: the same danger
 * block, the same "o que não é apagado" block, the same slug typed back exactly. Somebody who has
 * unlocked one of them should not have to learn the other.
 *
 * What it adds is the part that is counter-intuitive, and it is the whole reason this is called
 * "resetar" and not "apagar": **the v1 questionnaire survives**. If it did not, every test round
 * would begin by pasting the whole JSON back in — the opposite of what the button exists to spare.
 * And the survey **stays approved**, because demoting it would force a re-approval before every
 * round, which is the friction being removed.
 *
 * The three counters are not promised here. The dialog knows how many responses go — it is the
 * survey's own `responseCount` — but not how many versions had a questionnaire, and a number that
 * turns out wrong on an irreversible action is worse than no number. The `200` answers all three,
 * and the toast is where they are read back.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTModal, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import { formatCount } from '../../../Utils/surveyFormat';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
`;

const dangerStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const headlineStyle = css`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const lineStyle = css`
  margin: 0;
`;

const keepsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const labelStyle = css`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

export interface SurveyResetDialogProps {
  slug: string;
  /** Responses across every version and every status — what the reset deletes, to the row. */
  responseCount: number;
  /** The version being delivered now. Everything above version 1 goes. */
  version: number;
  /** The open draft, when there is one — it goes too. */
  draftVersion: number | null;
  isWorking: boolean;
  onReset: (confirmSlug: string) => void;
  onClose: () => void;
}

export const SurveyResetDialog = ({
  slug,
  responseCount,
  version,
  draftVersion,
  isWorking,
  onReset,
  onClose,
}: SurveyResetDialogProps): JSX.Element => {
  const [typed, setTyped] = useState('');

  /** Exact, deliberately: the BFF compares with `===`, and so does the button. */
  const unlocked = typed === slug;

  const highestVersion = Math.max(version, draftVersion ?? 0);

  /*
    Already where a reset would leave it. Version 1, no draft and no response means nothing to
    erase — the rewording records of R39 only exist for a version that has answers — so this is a
    state of its own rather than a slug field guarding a no-op.
  */
  const isAlreadyReset = highestVersion === 1 && responseCount === 0;

  if (isAlreadyReset) {
    return (
      <UTModal
        open
        position='center'
        maxWidth='480px'
        hasButtonClose
        modalContentId='survey-reset-dialog'
        closeButtonId='survey-reset-dialog-close'
        title='Resetar pesquisa'
        content={
          <div className={contentStyle}>
            <div className={keepsStyle}>
              <p className={headlineStyle}>Não há o que resetar</p>
              <p className={lineStyle}>
                Esta pesquisa já está no estado de recém-criada: está na versão 1, não tem rascunho
                aberto e não tem nenhuma resposta guardada.
              </p>
            </div>
          </div>
        }
        customButtons={[{ text: 'Fechar', variant: 'secondary', onClick: onClose }]}
        onClose={onClose}
      />
    );
  }

  const isOne = responseCount === 1;
  const countLabel = isOne ? '1 resposta' : `${formatCount(responseCount)} respostas`;

  /*
    Assembled rather than written twice: on a survey with no answers the responses half of the
    sentence has nothing to say, and leading with "serão apagadas 0 respostas" is how a dialog
    teaches people to stop reading it.
  */
  const headline =
    responseCount === 0
      ? `Somem todas as versões de ${slug} acima da versão 1. Não há como desfazer.`
      : `${isOne ? 'Será apagada' : 'Serão apagadas'} ${countLabel} de ${slug} — de todas as ` +
        'pessoas e de todas as versões — e todas as versões acima da versão 1. Não há como ' +
        'desfazer.';

  const cascade =
    `Vão junto as perguntas, as opções e as traduções dessas versões` +
    `${draftVersion === null ? '' : `, incluindo o rascunho da versão ${draftVersion}`}` +
    ', mais os registros de edição de texto.';

  return (
    <UTModal
      open
      position='center'
      maxWidth='560px'
      hasButtonClose
      modalContentId='survey-reset-dialog'
      closeButtonId='survey-reset-dialog-close'
      title='Resetar pesquisa'
      description='Devolve a pesquisa ao estado de recém-criada. Apaga mais que "Apagar respostas".'
      content={
        <div className={contentStyle}>
          <div className={dangerStyle} role='alert'>
            <p className={headlineStyle}>{headline}</p>
            <p className={lineStyle}>{cascade}</p>
            {/* Only when there is somebody to say it about: announcing that "quem já tinha
                respondido volta a ficar elegível" on a survey with no answers is a consequence
                declared to an empty room, and it makes the rest of the box read as boilerplate. */}
            {responseCount === 0 ? null : (
              <p className={lineStyle}>
                Quem já tinha respondido <strong>volta a ficar elegível</strong>: a pesquisa aparece
                de novo para essas pessoas.
              </p>
            )}
          </div>

          <div className={keepsStyle}>
            <p className={headlineStyle}>O que não é apagado</p>
            <p className={lineStyle}>
              A pesquisa, <strong>o questionário da versão 1</strong> e as traduções dele, o vínculo
              com a funcionalidade opt-in, o gatilho, a janela de coleta e o bloco de elegibilidade
              inteiro.
            </p>
            <p className={lineStyle}>
              E ela <strong>continua aprovada</strong>: resetar não rebaixa para rascunho e não
              aprova nada — o estado de aprovação fica como está.
            </p>
          </div>

          <div className={fieldStyle}>
            <span className={labelStyle}>
              Para liberar, digite <span className={slugStyle}>{slug}</span>
            </span>
            <UTTextField
              id='survey-reset-slug'
              placeholder={slug}
              autoComplete='off'
              value={typed}
              onChange={event => {
                setTyped(event.target.value);
              }}
            />
            <p className={hintStyle}>
              Exato, sem espaço sobrando e respeitando maiúsculas e minúsculas — é o mesmo texto que
              o BFF confere antes de apagar qualquer coisa.
            </p>
          </div>
        </div>
      }
      customButtons={[
        { text: 'Cancelar', variant: 'secondary', disabled: isWorking, onClick: onClose },
        {
          text: 'Resetar pesquisa',
          state: 'danger',
          loading: isWorking,
          disabled: !unlocked || isWorking,
          onClick: () => {
            onReset(typed);
          },
        },
      ]}
      onClose={onClose}
    />
  );
};
